/**
 * Capacity-periodization engine for NON-endurance goals (alpine / utförsåkning,
 * and — phase 3 — the IronMan fystest). The endurance engine (planGenerator.ts)
 * ramps CTL/TSS toward a single race day; that doesn't fit a strength/power/
 * anaerobic sport that peaks across a competition PERIOD. This builds a periodized
 * plan from the athlete's reusable building blocks instead:
 *
 *   phase (weeks-to-goal) → strength focus (strengthRoutines.phaseToFocus):
 *     base   → foundation (work capacity, strength endurance, agility, aerobic base)
 *     build  → max strength (heavy squat/bench/chins — IronMan max events)
 *     peak   → power / anaerobic (box jumps ≈ Kassehopp, agility, plyo)
 *     taper  → maintenance (prime, low volume)
 *     race   → maintenance through the competition period [date, endDate]
 *   every 4th week → cutback (deload).
 *
 * Each week: 2 strength sessions (alpine-emphasis routines), 1 quality run +
 * 1 easy run for aerobic capacity (the 3000 m IronMan event / recovery between
 * runs). Output shape matches generateTrainingPlan so it slots into RacePlan /
 * publishPlan unchanged.
 */
import {
  RaceTarget, PlannedWeek, PlannedSession, PreferredDays, BlockedRange, TrainingPhase,
} from "../types/training";
import { pickStrengthRoutine, buildGymDescription, strengthFocusLabel, phaseToFocus } from "./strengthRoutines";
import { pickIntervalSession } from "./workoutBank";
import { toIcuWorkout, parseStructuredWorkout } from "./planGenerator";
import { isoWeekKey } from "./planWeekMeta";

const DAY = 86400000;
const toStr = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY);
const mondayOf = (d: Date) => addDays(d, -(((d.getDay() + 6) % 7)));
const weekLabel = (key: string) => `V${key.slice(-2)}`;

/**
 * The next snow/competition season window [start, end] whose end is still in the
 * future — used as the default alpine "peak period" target when utförsåkning is
 * the primary sport. Defaults to Nov–Apr (matches the profile's snow months).
 */
export function nextSnowSeason(today: Date, snowStart = 11, snowEnd = 4): { start: string; end: string } {
  const wraps = snowStart > snowEnd; // e.g. Nov(11)→Apr(4) crosses the year
  for (let dy = -1; dy <= 2; dy++) {
    const y = today.getFullYear() + dy;
    const start = new Date(y, snowStart - 1, 1);
    const end = new Date(wraps ? y + 1 : y, snowEnd, 0); // day 0 → last day of snowEnd month
    if (end >= today) return { start: toStr(start), end: toStr(end) };
  }
  const y = today.getFullYear();
  return { start: toStr(new Date(y, snowStart - 1, 1)), end: toStr(new Date(wraps ? y + 1 : y, snowEnd, 0)) };
}

export interface CapacityPlanResult {
  weeks: PlannedWeek[];
  sessions: PlannedSession[];
  flaggedDates: Map<string, string>;
  expectedRaceTSB: number;
  volumeNote?: string;
  plannedHardPct: number;
  historicalHardPct?: number;
}

/** Phase for a week given how many whole weeks remain until the goal day. */
function phaseForWeeksToGo(wtg: number, weekIndex: number): TrainingPhase {
  if (wtg < 0) return "race";        // weeks AFTER the goal — competition period
  if (wtg <= 1) return "taper";      // goal week + the week before
  if (weekIndex > 0 && weekIndex % 4 === 3) return "cutback"; // deload every 4th
  if (wtg <= 4) return "peak";
  if (wtg <= 10) return "build";
  return "base";
}

const RUN_SPEC = (phase: TrainingPhase): "general" | "threshold" | "race" =>
  phase === "base" ? "general" : phase === "peak" || phase === "race" || phase === "taper" ? "race" : "threshold";

export function generateCapacityPlan(opts: {
  goal: RaceTarget;
  today: Date;
  startFrom?: Date;
  currentCTL?: number;
  preferredDays?: PreferredDays;
  blockedRanges?: BlockedRange[];
  sickSince?: string | null;
}): CapacityPlanResult {
  const { goal } = opts;
  const today = opts.startFrom ?? opts.today;
  const todayStr = toStr(opts.today);
  const ctl = opts.currentCTL ?? 45;
  const goalDate = new Date(goal.date + "T12:00:00");
  const periodEnd = new Date((goal.endDate ?? goal.date) + "T12:00:00");
  const periodEndStr = toStr(periodEnd);

  const blocked = new Set<string>();
  for (const r of opts.blockedRanges ?? []) {
    let d = new Date(r.start + "T12:00:00");
    const end = new Date(r.end + "T12:00:00");
    while (d <= end) { blocked.add(toStr(d)); d = addDays(d, 1); }
  }

  // Preferred days (Mon=0..Sun=6): gym → strength, hard → quality run, easy → easy run.
  const pd = opts.preferredDays;
  const strengthDays = (pd?.gym && pd.gym.length ? pd.gym : [1, 4]).slice(0, 3); // up to Tue/Fri
  const qualityDay = (pd?.hard && pd.hard.length ? pd.hard[0] : 2);              // Wed
  const easyDay = (pd?.easy && pd.easy.length ? pd.easy[pd.easy.length - 1] : 5); // Sat

  const sessions: PlannedSession[] = [];
  const weeks: PlannedWeek[] = [];
  const flaggedDates = new Map<string, string>();

  const firstMonday = mondayOf(today);
  const lastMonday = mondayOf(periodEnd);
  const totalWeeks = Math.max(1, Math.round((lastMonday.getTime() - firstMonday.getTime()) / (7 * DAY)) + 1);

  let hardMin = 0, totalMin = 0;
  const sick = opts.sickSince ?? null;

  const push = (s: PlannedSession, hard: boolean) => {
    sessions.push(s);
    totalMin += s.durationHours * 60;
    if (hard) hardMin += s.durationHours * 60;
  };

  for (let wi = 0; wi < totalWeeks; wi++) {
    const monday = addDays(firstMonday, wi * 7);
    const weekKey = isoWeekKey(toStr(monday));
    const wtg = Math.round((mondayOf(goalDate).getTime() - monday.getTime()) / (7 * DAY));
    const phase = phaseForWeeksToGo(wtg, wi);
    const focus = phaseToFocus(phase);
    weeks.push({ weekKey, label: weekLabel(weekKey), targetTSS: 0, targetHours: 0, phase });

    const onDay = (offset: number) => toStr(addDays(monday, offset));
    const usable = (date: string) =>
      date >= todayStr && date <= periodEndStr && !blocked.has(date) && !(sick && date >= sick && date <= todayStr);

    // Strength — 2 sessions (1 in cutback/race weeks), alpine-emphasis routines.
    const nStrength = phase === "cutback" || phase === "race" ? 1 : 2;
    let placed = 0;
    for (const off of strengthDays) {
      if (placed >= nStrength) break;
      const date = onDay(off);
      if (!usable(date)) continue;
      const routine = pickStrengthRoutine(phase, goal.sport, `${wi}-${off}`);
      push({
        name: `Gym · ${strengthFocusLabel(routine.focus)}`,
        date, weekKey, sport: "WeightTraining",
        durationHours: 1.0, targetIF: 0.6, estimatedTSS: 40,
        description: toIcuWorkout(buildGymDescription(routine, "1h")),
      }, focus === "power" || focus === "max-strength");
      placed++;
    }

    // Quality run — aerobic capacity for the 3000 m (skip in cutback/race weeks).
    if (phase !== "cutback" && phase !== "race") {
      const date = onDay(qualityDay);
      if (usable(date)) {
        const hours = 0.85;
        const picked = pickIntervalSession("Run", RUN_SPEC(phase), "5k", ctl, hours, date, 0);
        const desc = toIcuWorkout(picked.description);
        const parsed = parseStructuredWorkout(desc);
        push({
          name: picked.name, date, weekKey, sport: "Run",
          durationHours: parsed?.durationHours ?? hours,
          targetIF: 0.9, estimatedTSS: parsed?.tss ?? 55, description: desc,
        }, true);
      }
    }

    // Easy run — recovery / aerobic base.
    {
      const date = onDay(easyDay);
      if (usable(date)) {
        const hours = phase === "race" || phase === "taper" ? 0.5 : 0.75;
        const dur = hours === 0.5 ? "30min" : "45min";
        push({
          name: "Lugn löpning", date, weekKey, sport: "Run",
          durationHours: hours, targetIF: 0.68, estimatedTSS: Math.round(0.68 * 0.68 * hours * 100),
          description: toIcuWorkout(`${dur} Z2 HR lugnt`),
        }, false);
      }
    }

    if (phase === "race") {
      flaggedDates.set(onDay(qualityDay), "Tävlingsperiod — håll formen, kör tävling/teknik och lätt underhåll. Ingen tung uppbyggnad nu.");
    }
  }

  // Roll the per-week targets up from the placed sessions.
  const byWeek = new Map<string, { h: number; tss: number }>();
  for (const s of sessions) {
    const w = byWeek.get(s.weekKey) ?? { h: 0, tss: 0 };
    w.h += s.durationHours; w.tss += s.estimatedTSS;
    byWeek.set(s.weekKey, w);
  }
  for (const w of weeks) {
    const agg = byWeek.get(w.weekKey);
    w.targetHours = Math.round((agg?.h ?? 0) * 4) / 4;
    w.targetTSS = Math.round(agg?.tss ?? 0);
  }

  const plannedHardPct = totalMin > 0 ? Math.round((hardMin / totalMin) * 100) : 0;
  const note = goal.kind === "ironman"
    ? `IronMan-fystest ${goal.date}: planen tränar alla åtta testmoment — maxstyrka (benböj/bänk), uthållighetsstyrka, chins, bål, anaerob kapacitet (Kassehopp), koordination (Hexagon) och 3000 m. Se IronMan-panelen för moment-för-moment.`
    : goal.endDate
    ? `Tävlingsperiod ${goal.date} – ${goal.endDate}: planen toppar mot periodstarten och håller formen genom säsongen.`
    : `Toppar mot ${goal.date}.`;

  return {
    weeks, sessions, flaggedDates,
    expectedRaceTSB: 0,
    volumeNote: note,
    plannedHardPct,
  };
}
